import React from 'react';
import { Dialog } from 'primereact/dialog';
import type { PracticalMaterial, PracticalVideo } from '../StudentPracticalMaterials';

/* ─── Design tokens (matches ViewStudyMaterialModal) ─────────────────── */
const C = {
  navy: '#0e225d', navyMid: '#1a3370', navyDark: '#0a1a3d',
  gold: '#fbb21f', goldDim: 'rgba(251,178,31,0.16)', goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff', border: '#e5e7eb', bg: '#f5f7fa', muted: '#9ca3af', sub: '#6b7280',
};

const formatDate = (s: string) => {
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
};

const getFileIcon = (m: string) =>
  m?.includes('pdf') ? 'pi pi-file-pdf' : m?.includes('word') ? 'pi pi-file-word'
  : m?.includes('excel') || m?.includes('spreadsheet') ? 'pi pi-file-excel'
  : m?.includes('image') ? 'pi pi-image' : 'pi pi-file';

const getFileIconColor = (m: string) =>
  m?.includes('pdf') ? '#dc2626' : m?.includes('word') ? '#2563eb'
  : m?.includes('excel') || m?.includes('spreadsheet') ? '#16a34a'
  : m?.includes('image') ? '#7c3aed' : C.navy;

const Section = ({ icon, title, count, children }: { icon: string; title: string; count: number; children: React.ReactNode }) => (
  <div style={{ marginBottom: '14px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      <div style={{ width: '3px', height: '14px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
      <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, display: 'flex', alignItems: 'center', gap: '6px' }}>
        <i className={icon} style={{ fontSize: '0.72rem' }} />
        {title}
        <span style={{ padding: '1px 7px', borderRadius: '10px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, color: '#92580a', fontSize: '0.68rem', fontWeight: '700' }}>{count}</span>
      </span>
    </div>
    {children}
  </div>
);

const actionLink: React.CSSProperties = {
  padding: '6px 12px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, color: C.white,
  textDecoration: 'none', borderRadius: '7px', fontSize: '0.72rem', fontWeight: '700',
  display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0,
};

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px',
  background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px',
};

interface ViewPracticalMaterialModalProps {
  visible: boolean;
  material: PracticalMaterial | null;
  onHide: () => void;
}

const ViewPracticalMaterialModal: React.FC<ViewPracticalMaterialModalProps> = ({ visible, material, onHide }) => {
  if (!material) return null;

  const videos     = material.videos      || [];
  const files      = material.files       || [];
  const videoLinks = material.video_links || [];
  const hasBody    = !!material.body && material.body.trim() !== '' && material.body.trim() !== '.';

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: '92vw', maxWidth: '780px' }}
      dismissableMask
      draggable={false}
      header={false}
      pt={{
        root:    { style: { borderRadius: '18px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(14,34,93,0.22)', border: `1px solid ${C.border}` } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: 0 } },
      }}
    >
      <div>
        {/* Header */}
        <div style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 55%, ${C.navyDark} 100%)`, padding: '22px 24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', background: C.goldDim, borderRadius: '50%', filter: 'blur(35px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: C.gold }}>
                  <i className="pi pi-briefcase" style={{ fontSize: '0.62rem' }} />
                  Practical Material
                </div>
                {material.scope_type === 'course' && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)' }}>
                    General
                  </div>
                )}
              </div>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: C.white, lineHeight: '1.35', letterSpacing: '-0.3px' }}>{material.title}</h2>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', fontWeight: '500' }}>
                <i className="pi pi-calendar" style={{ fontSize: '0.62rem' }} />
                {formatDate(material.created_at)}
              </span>
            </div>
            <button
              onClick={onHide}
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.65)', flexShrink: 0 }}
            >
              <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', maxHeight: '60vh', overflowY: 'auto' }}>
          {hasBody && (
            <div style={{ marginBottom: '16px', padding: '14px 16px', background: C.bg, borderRadius: '10px', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <div style={{ width: '3px', height: '14px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: C.navy }}>
                  <i className="pi pi-align-left" style={{ fontSize: '0.7rem', marginRight: '5px' }} />
                  Instructions
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: C.sub, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{material.body}</p>
            </div>
          )}

          {videos.length > 0 && (
            <Section icon="pi pi-video" title="Videos" count={videos.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {videos.map((video: PracticalVideo, index: number) => {
                  const ready = video.status === 'ready';
                  return (
                    <div key={video.video_id || index} style={rowStyle}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="pi pi-play-circle" style={{ fontSize: '0.95rem', color: C.gold }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: '600', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px' }}>
                          {video.original_name || `Video ${index + 1}`}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {video.size ? <span style={{ fontSize: '0.68rem', color: C.muted }}>{formatFileSize(video.size)}</span> : null}
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 7px', borderRadius: '10px', background: ready ? 'rgba(22,163,74,0.1)' : 'rgba(251,178,31,0.12)', border: `1px solid ${ready ? 'rgba(22,163,74,0.25)' : C.goldBorder}`, fontSize: '0.65rem', fontWeight: '700', color: ready ? '#15803d' : '#92580a' }}>
                            <i className={`pi ${ready ? 'pi-check-circle' : 'pi-clock'}`} style={{ fontSize: '0.58rem' }} />
                            {ready ? 'Ready' : video.status === 'processing' ? 'Processing' : video.status}
                          </span>
                        </div>
                      </div>
                      {ready && video.m3u8_url && (
                        <a href={video.m3u8_url} target="_blank" rel="noopener noreferrer" style={actionLink}>
                          <i className="pi pi-play" style={{ fontSize: '0.65rem' }} />
                          Play
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {files.length > 0 && (
            <Section icon="pi pi-paperclip" title="Attachments" count={files.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {files.map((file, index) => (
                  <div key={index} style={rowStyle}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: C.bg, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={getFileIcon(file.mime_type)} style={{ fontSize: '1rem', color: getFileIconColor(file.mime_type) }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: '600', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px' }}>{file.file_name}</div>
                      <div style={{ fontSize: '0.68rem', color: C.muted }}>{formatFileSize(file.size)}</div>
                    </div>
                    <a href={file.url} target="_blank" rel="noopener noreferrer" style={actionLink}>
                      <i className="pi pi-download" style={{ fontSize: '0.65rem' }} />
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {videoLinks.length > 0 && (
            <Section icon="pi pi-link" title="Video Links" count={videoLinks.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {videoLinks.map((link, index) => (
                  <a key={index} href={link} target="_blank" rel="noopener noreferrer" style={{ ...rowStyle, color: C.navy, textDecoration: 'none', fontSize: '0.8rem', gap: '10px' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="pi pi-external-link" style={{ fontSize: '0.72rem', color: '#92580a' }} />
                    </div>
                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.78rem', color: C.sub }}>{link}</span>
                  </a>
                ))}
              </div>
            </Section>
          )}

          {!hasBody && videos.length === 0 && files.length === 0 && videoLinks.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ width: '44px', height: '44px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                <i className="pi pi-inbox" style={{ fontSize: '1.2rem', color: C.navy, opacity: 0.25 }} />
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: C.muted }}>No content available for this material</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', background: C.bg }}>
          <button
            onClick={onHide}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 22px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.22)' }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.72rem' }} />
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewPracticalMaterialModal;
